import React from 'react';
import { motion } from 'framer-motion';
import {
  Users, Award, Target, Heart,
  Building2, GraduationCap, Briefcase,
  Globe2, Rocket, Shield, HandshakeIcon,
  LineChart, UserCircle2, Phone, Mail, MapPin, ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

const ValueCard = ({ icon: Icon, title, description }) => (
  <motion.div
    className="relative"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
  >
    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-indigo-900/50 to-purple-900/50 p-1">
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 blur-xl" />
      <div className="relative bg-gray-900 p-6 rounded-lg text-center">
        <div className="mb-4 inline-block p-3 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-lg">
          <Icon className="w-6 h-6 text-indigo-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
        <p className="text-gray-300">{description}</p>
      </div>
    </div>
  </motion.div>
);

const MilestoneCard = ({ year, title, description, icon: Icon }) => (
  <motion.div
    className="relative flex items-center gap-6"
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
  >
    <div className="relative">
      <div className="absolute inset-0 bg-indigo-500 blur-lg opacity-50" />
      <div className="relative w-16 h-16 flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg">
        <Icon className="w-8 h-8 text-white" />
      </div>
    </div>
    <div>
      <div className="text-sm text-indigo-400 mb-1">{year}</div>
      <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
      <p className="text-gray-300">{description}</p>
    </div>
  </motion.div>
);

// Add a new component for the problem cards with a fresh design
const ProblemCard = ({ icon: Icon, title, description }) => (
  <motion.div
    className="relative"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
  >
    <div className="group relative p-8 h-full">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/10 to-purple-900/10 
                      group-hover:from-indigo-900/20 group-hover:to-purple-900/20 
                      transition-all duration-300 rounded-xl" />
      
      {/* Content */}
      <div className="relative flex flex-col h-full">
        <div className="flex items-center mb-6">
          <div className="mr-4 p-3 rounded-xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10">
            <Icon className="w-6 h-6 text-indigo-400" />
          </div>
          <h3 className="text-xl font-bold text-white">{title}</h3>
        </div>
        <p className="text-gray-400 leading-relaxed">{description}</p>
      </div>
    </div>
  </motion.div>
);

function About() {
  const teamMembers = [
    {
      name: "Sarah Chen",
      role: "Chief Executive Officer",
      linkedin: "https://linkedin.com/in/sarah-chen"
    },
    {
      name: "Michael Rodriguez",
      role: "Chief Technology Officer",
      linkedin: "https://linkedin.com/in/michael-rodriguez"
    },
    {
      name: "Emma Thompson",
      role: "Head of Operations",
      linkedin: "https://linkedin.com/in/emma-thompson"
    }
  ];

  const platformValues = [
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Azure VNET architecture with segregated subnets, private endpoints, and comprehensive RBAC controls."
    },
    {
      icon: Rocket,
      title: "Real-Time Operations",
      description: "WebSocket-powered real-time network enabling instant updates across loads, bids, telematics, and billing."
    },
    {
      icon: LineChart,
      title: "Scalable Architecture",
      description: "Cloud-native infrastructure designed for petabyte-scale data processing and millions of transactions."
    },
    {
      icon: HandshakeIcon,
      title: "Industry Expertise",
      description: "Built by logistics veterans with 30+ years of experience in freight forwarding and supply chain operations."
    }
  ];

  const capabilities = [
    {
      year: "Core Platform",
      title: "Integrated Logistics Network",
      description: "Custom-built load posting, bidding, and real-time tracking system with enterprise-grade security.",
      icon: Globe2
    },
    {
      year: "Documentation",
      title: "Automated Document Management",
      description: "Intelligent document generation, secure storage, and real-time contract updates with dispute resolution.",
      icon: Building2
    },
    {
      year: "Mobile Solutions",
      title: "Cross-Platform Mobile Access",
      description: "Native iOS and Android applications for real-time driver updates, BOL/POD management, and GPS tracking.",
      icon: Rocket
    },
    {
      year: "Integration",
      title: "Enterprise API Platform",
      description: "Robust API client enabling real-time data feeds and seamless system integration capabilities.",
      icon: LineChart
    }
  ];

  const stats = [
    { label: "Industry Experience", value: "30+" },
    { label: "Data Centers", value: "Global" },
    { label: "Uptime", value: "99.99%" },
    { label: "Enterprise Clients", value: "Growing" }
  ];

  const problems = [
    {
      icon: Users,
      title: "Manual Process Overload",
      description: "Replace 6+ disconnected systems and 4+ hours of daily manual work with a single, real-time digital workflow."
    },
    {
      icon: Target,
      title: "Revenue Leakage",
      description: "Capture 15-20% more revenue through automated rate management, dispute prevention, and real-time billing updates."
    },
    {
      icon: Award,
      title: "Communication Chaos",
      description: "Eliminate 90% of check-calls and emails with instant load updates, automated notifications, and real-time tracking."
    },
    {
      icon: Heart,
      title: "Scale Limitations",
      description: "Handle 10x more loads without adding headcount through intelligent automation and predictive operations."
    }
  ];

  return (
    <div className="bg-gray-900">
      {/* Enhanced Hero Section */}
      <section className="relative min-h-[80vh] flex items-center">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-30" />
          {/* Add animated particles */}
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full"
              animate={{
                x: [0, Math.random() * 100 - 50],
                y: [0, Math.random() * 100 - 50],
                scale: [1, Math.random() + 0.5],
                opacity: [0.5, 0]
              }}
              transition={{
                duration: Math.random() * 2 + 2,
                repeat: Infinity,
                repeatType: "reverse"
              }}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`
              }}
            />
          ))}
        </div>
        
        <div className="relative container mx-auto px-4 py-20">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-block mb-6"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-indigo-500 blur-xl opacity-50" />
                <div className="relative bg-gradient-to-br from-indigo-500 to-purple-500 p-3 rounded-lg">
                  <Building2 className="w-10 h-10 text-white" />
                </div>
              </div>
            </motion.div>
            
            <motion.h1 
              className="text-4xl md:text-6xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              Enterprise-Grade{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                Logistics Network
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-xl text-gray-300 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              A real-time freight forwarding platform built for enterprise scale. 
              Powered by cutting-edge technology and backed by decades of industry expertise.
            </motion.p>

            {/* Update stats section */}
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mt-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              {stats.map((stat, index) => (
                <div key={index} className="p-4 rounded-lg bg-white/5 backdrop-blur-sm">
                  <div className="text-2xl font-bold text-indigo-400 mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* New Problems We Solve Section - Add after hero, before Platform Capabilities */}
      <section className="py-20 relative overflow-hidden">
        {/* Background with subtle animated gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-indigo-900/20 to-purple-900/20" />
        
        <div className="container mx-auto px-4 relative">
          <motion.div 
            className="max-w-3xl mx-auto text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">Built By Industry Veterans</h2>
            <p className="text-xl text-gray-300">
              30+ years of logistics expertise meets modern software architecture. 
              We've lived these problems—now we're solving them.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {problems.map((problem, index) => (
              <ProblemCard key={index} {...problem} />
            ))}
          </div>

          <motion.div 
            className="mt-16 text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-gray-400 leading-relaxed">
              Every feature in our platform was born from real-world logistics challenges. 
              We've replaced manual processes with intelligent automation, transforming 
              how modern freight operations scale.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Platform Capabilities Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-indigo-900">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-6">Platform Capabilities</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              A comprehensive suite of integrated logistics solutions
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-12">
            {capabilities.map((capability, index) => (
              <MilestoneCard key={index} {...capability} />
            ))}
          </div>
        </div>
      </section>

      {/* Technical Excellence Section - Updated with centered content */}
      <section className="py-20 bg-gradient-to-b from-indigo-900 to-purple-900">
        <div className="container mx-auto px-4">
          <motion.div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-6">Technical Excellence</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Built for enterprise-scale performance and reliability
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {platformValues.map((value, index) => (
              <ValueCard key={index} {...value} />
            ))}
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="bg-gray-900 border-t border-white/10">
        <div className="container mx-auto px-4 py-12 md:py-20">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8 mb-12 md:mb-20">
            {/* Company Info */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">
                LoadLink
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Enterprise-grade logistics platform with real-time operations, custom-built infrastructure, and intelligent automation.
              </p>
              <div className="flex space-x-4">
                {/* Social Media Links */}
                {[
                  { icon: 'M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z', name: 'Twitter' },
                  { icon: 'M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z', name: 'LinkedIn' },
                  { icon: 'M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z', name: 'GitHub' }
                ].map((social) => (
                  <a
                    key={social.name}
                    href="#"
                    className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors duration-300"
                    aria-label={social.name}
                  >
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d={social.icon} />
                    </svg>
                  </a>
                ))}
              </div>
            </div>

            {/* Product Features */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6">Product Features</h4>
              <ul className="space-y-4">
                {[
                  'Real-Time Operations',
                  'Load Management',
                  'Billing & Disputes',
                  'Document Control',
                  'Mobile Applications',
                  'Telematics Integration'
                ].map((item) => (
                  <li key={item}>
                    <Link
                      to={`/features/${item.toLowerCase().replace(/\s+/g, '-')}`}
                      className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <ArrowRight className="w-4 h-4 mr-2 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                      {item}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Enterprise */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6">Enterprise</h4>
              <ul className="space-y-4">
                {[
                  'Security & Compliance',
                  'API Documentation',
                  'User Management',
                  'Custom Integration',
                  'Admin Controls',
                  'Analytics Dashboard'
                ].map((item) => (
                  <li key={item}>
                    <Link
                      to={`/enterprise/${item.toLowerCase().replace(/\s+/g, '-')}`}
                      className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <ArrowRight className="w-4 h-4 mr-2 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                      {item}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6">Contact Us</h4>
              <ul className="space-y-4">
                <li>
                  <a href="tel:+1234567890" className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center">
                    <Phone className="w-4 h-4 mr-2" />
                    +1 (234) 567-890
                  </a>
                </li>
                <li>
                  <a href="mailto:enterprise@loadlink.com" className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center">
                    <Mail className="w-4 h-4 mr-2" />
                    enterprise@loadlink.com
                  </a>
                </li>
                <li className="flex items-start">
                  <MapPin className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-400">
                    Enterprise Solutions Division<br />
                    LoadLink Technologies<br />
                    San Francisco, CA 94105
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/10">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-gray-400 text-sm">
                © {new Date().getFullYear()} LoadLink. Enterprise-grade logistics platform.
              </div>
              <div className="flex space-x-6">
                {[
                  'Terms of Service', 
                  'Privacy Policy', 
                  'Security', 
                  'API Status'
                ].map((item) => (
                  <Link
                    key={item}
                    to={`/${item.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-gray-400 hover:text-white text-sm transition-colors duration-300"
                  >
                    {item}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default About; 